import { useState, useEffect, useCallback, useContext } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Shield, ShieldOff, RefreshCw,
  UserCheck, Mail, User as UserIcon, Settings, AlertCircle, Activity,
  AppWindow, ExternalLink, Globe, Lock, Users
} from 'lucide-react'
import {
  getUserDetail, impersonateUser, updateUser, getUserActivityLog,
  listAllApps,
  type UserDetail as UserDetailType, type AuditLogEvent, type AdminApp
} from '../../lib/api'
import { UserContext } from '../../hooks/useUser'
import ActivityTimeline from '../../components/ActivityTimeline'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleString()
}

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const currentUser = useContext(UserContext)

  const [user, setUser] = useState<UserDetailType | null>(null)
  const [activityEvents, setActivityEvents] = useState<AuditLogEvent[]>([])
  const [apps, setApps] = useState<AdminApp[]>([])
  const [loading, setLoading] = useState(true)
  const [activityLoading, setActivityLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadUser = useCallback(async () => {
    if (!userId) return
    try {
      const [userData, appsData] = await Promise.all([
        getUserDetail(parseInt(userId)),
        listAllApps(parseInt(userId)).catch(() => []),
      ])
      setUser(userData)
      setApps(appsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user')
    } finally {
      setLoading(false)
    }
  }, [userId])

  const loadActivity = useCallback(async () => {
    if (!userId) return
    setActivityLoading(true)
    try {
      const data = await getUserActivityLog(parseInt(userId), { limit: 20 })
      setActivityEvents(data.events)
    } catch (err) {
      // Ignore errors
    } finally {
      setActivityLoading(false)
    }
  }, [userId])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  async function handleImpersonate() {
    if (!userId) return
    setActionLoading('impersonate')
    setError(null)

    try {
      await impersonateUser(parseInt(userId))
      // impersonateUser redirects
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to impersonate')
      setActionLoading(null)
    }
  }

  async function handleToggleAdmin() {
    if (!userId || !user) return
    setActionLoading('toggle-admin')
    try {
      await updateUser(parseInt(userId), { is_admin: !user.is_admin })
      await loadUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleToggleActive() {
    if (!userId || !user) return
    setActionLoading('toggle-active')
    try {
      await updateUser(parseInt(userId), { is_active: !user.is_active })
      await loadUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">User not found</h2>
        <Link to="/admin/users" className="text-primary hover:underline">
          Back to Users
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/users')}
          className="p-2 rounded-lg hover:bg-secondary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">
            {user.settings?.first_name && user.settings?.last_name
              ? `${String(user.settings.first_name)} ${String(user.settings.last_name)}`
              : String(user.settings?.first_name || user.name || user.email.split('@')[0])}
          </h1>
          <p className="text-muted-foreground">{user.email}</p>
        </div>
        {currentUser?.email !== user.email && (
          <ConfirmDialog
            title="Impersonate User"
            description={`Start impersonating ${String(user.settings?.first_name || user.name || user.email)}? You will be logged in as them.`}
            confirmLabel="Impersonate"
            onConfirm={handleImpersonate}
            trigger={
              <button
                disabled={actionLoading === 'impersonate'}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                <UserCheck className="w-4 h-4" />
                Impersonate
              </button>
            }
          />
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Info Card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            User Information
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Email</span>
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                {user.email}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">User ID</span>
              <span className="font-mono text-sm">{user.id}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <button
                onClick={handleToggleActive}
                disabled={actionLoading === 'toggle-active'}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  user.is_active
                    ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {user.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Role</span>
              <button
                onClick={handleToggleAdmin}
                disabled={actionLoading === 'toggle-admin'}
                className={`flex items-center gap-1 px-3 py-1 rounded text-sm transition-colors ${
                  user.is_admin
                    ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {user.is_admin ? (
                  <>
                    <Shield className="w-3 h-3" /> Admin
                  </>
                ) : (
                  <>
                    <ShieldOff className="w-3 h-3" /> User
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Created</span>
              <span className="text-sm">{formatDate(user.created_at)}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last Login</span>
              <span className="text-sm">{formatDate(user.last_login)}</span>
            </div>
          </div>

          {/* Settings JSON */}
          {Object.keys(user.settings).length > 0 && (
            <div className="mt-6 pt-4 border-t border-border">
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </h3>
              <pre className="text-xs bg-secondary p-3 rounded-lg overflow-auto max-h-40">
                {JSON.stringify(user.settings, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Apps Card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AppWindow className="w-5 h-5" />
            Apps ({apps.length})
          </h2>

          {apps.length === 0 ? (
            <p className="text-muted-foreground text-sm">No apps created</p>
          ) : (
            <div className="space-y-3">
              {apps.map((app) => {
                const statusColor = app.status === 'running' ? 'bg-green-500' :
                  app.status === 'starting' ? 'bg-amber-500' :
                  app.status === 'error' ? 'bg-red-500' : 'bg-gray-500'
                const AccessIcon = app.access_level === 'public' ? Globe :
                  app.access_level === 'platform' ? Users :
                  app.access_level === 'specific' ? UserCheck : Lock

                return (
                  <Link
                    key={app.id}
                    to={`/apps/${app.name}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className={`w-2 h-2 rounded-full ${statusColor}`} />
                        {app.status === 'running' && (
                          <div className={`absolute inset-0 w-2 h-2 rounded-full ${statusColor} animate-ping`} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{app.display_name || app.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">/a/{app.name}/</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <AccessIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 rounded hover:bg-secondary"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>


        {/* User Activity Card */}
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5" />
              User Activity
            </h2>
            <button
              onClick={loadActivity}
              disabled={activityLoading}
              className="p-2 rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
              title="Refresh activity"
            >
              <RefreshCw className={`w-4 h-4 ${activityLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <ActivityTimeline events={activityEvents} loading={activityLoading} />
        </div>

      </div>
    </div>
  )
}
